// src/components/common/Button.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import '../../styles/Button.css';

/**
 * High-Agency 8-State Interactive Button
 * Features:
 * - Island pill geometry with concentric nesting
 * - Optional "Button-in-Button" trailing icon with kinetic tension
 * - 8 reachable states: Idle, Hover, Focused, Pressed, Loading, Success, Failure, Disabled
 * - Spring press physics (scale: 0.98 on active)
 */
export default function Button({
  children,
  to,
  onClick,
  type = 'button',
  variant = 'primary', // 'primary' | 'secondary' | 'ghost' | 'danger' | 'success'
  size = 'md',        // 'sm' | 'md' | 'lg'
  icon = null,        // Optional trailing icon component/element
  trailingIcon = null,
  loading = false,
  success = false,
  error = false,
  disabled = false,
  className = '',
  ...rest
}) {
  const activeIcon = icon || trailingIcon;

  const stateClass = loading
    ? 'button--loading'
    : success
    ? 'button--success'
    : error
    ? 'button--error'
    : '';

  const buttonClasses = [
    'button',
    `button--${variant}`,
    `button--${size}`,
    stateClass,
    activeIcon ? 'button--has-icon' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {loading ? (
        <span className="button__spinner" aria-hidden="true">
          <span className="button__spinner-dot" />
          <span className="button__spinner-dot" />
          <span className="button__spinner-dot" />
        </span>
      ) : (
        <span className="button__label">{children}</span>
      )}

      {activeIcon && !loading && (
        <span className="button__icon-circle" aria-hidden="true">
          {activeIcon}
        </span>
      )}
    </>
  );

  if (to && !disabled && !loading) {
    return (
      <Link to={to} className={buttonClasses} {...rest}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading}
      aria-disabled={disabled || loading}
      {...rest}
    >
      {content}
    </button>
  );
}